import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ElementRef, MouseEvent, RefObject } from 'react'
import { clsx } from 'clsx'
import SharedContext from '../../../../shared/Context'
import type { FieldStringProps as StringFieldProps } from '../String'
import StringField from '../String'
import useId from '../../../../shared/helpers/useId'

import type { InputProps } from '../../../../components/Input'
import { SubmitButton } from '../../../../components/Input'
import Icon from '../../../../components/icon/Icon'
import IconPrimary from '../../../../components/icon-primary/IconPrimary'
import { view, hide, view_medium, hide_medium } from '../../../../icons'
import useTranslation from '../../hooks/useTranslation'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

const passwordIcon = Icon.transition({ view, hide })
const passwordIconLarge = Icon.transition({
  view: view_medium,
  hide: hide_medium,
})

export type PasswordVisibilityEvent = MouseEvent<HTMLButtonElement> & {
  value: string
}

export type PasswordProps = Omit<StringFieldProps, 'ref'> & {
  /**
   * Will be called when the user toggles the password to be visible.
   */
  onShowPassword?: (event: PasswordVisibilityEvent) => void
  /**
   * Will be called when the user toggles the password to be hidden.
   */
  onHidePassword?: (event: PasswordVisibilityEvent) => void
  /**
   * The sizes you can choose are `small` (1.5rem), `default` (2rem), `medium` (2.5rem) and `large` (3rem). Defaults to `default` / `null`. Also, if you define a number like `size="2"` then it will be forwarded as the input element attribute. Consider rather setting field sizes with [Form.Appearance](/uilib/extensions/forms/Form/Appearance/).
   */
  size?: InputProps['size']
  /**
   * `ElementRef` passed on to the password `input` element.
   */
  ref?: RefObject<HTMLInputElement>
}

function Password({
  id,
  className,
  ref: refProp,
  value,
  label,
  disabled,
  size,
  ...props
}: PasswordProps) {
  const generatedId = useId()
  const idToUse = id || generatedId

  const [hidden, setHidden] = useState<boolean>(true)

  const sharedContext = useContext(SharedContext)
  const translations = useTranslation().Password

  const ref = useRef<ElementRef<'input'>>(refProp?.current ?? null)

  const errorMessages = useMemo(() => {
    return {
      'Field.errorRequired': translations.errorRequired,
      ...props.errorMessages,
    }
  }, [translations.errorRequired, props.errorMessages])

  const toggleVisibility = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { onShowPassword, onHidePassword } = props

      const value = ref.current.value

      setHidden((hidden) => {
        hidden
          ? onShowPassword?.({ ...event, value })
          : onHidePassword?.({ ...event, value })

        return !hidden
      })

      if (ref.current) {
        ref.current.focus()
      }
    },
    [props]
  )

  const preventFocusChange = useCallback((event: MouseEvent) => {
    // Prevent the button from stealing focus from the input on click.
    // Without this, the input blurs and triggers a re-render that
    // replaces the icon SVG between mousedown and mouseup, causing
    // the browser to swallow the click event.
    event.preventDefault()
  }, [])

  const toggleVisibilityButton = useMemo(() => {
    return (
      <SubmitButton
        id={idToUse + '-submit-button'}
        type="button"
        variant="secondary"
        aria-controls={idToUse}
        aria-label={
          hidden ? translations.ariaLabelShow : translations.ariaLabelHide
        }
        icon={
          <IconPrimary
            icon={size === 'large' ? passwordIconLarge : passwordIcon}
            transitionState={hidden ? 'view' : 'hide'}
          />
        }
        disabled={disabled}
        skeleton={sharedContext.skeleton}
        onMouseDown={preventFocusChange}
        onClick={toggleVisibility}
      />
    )
  }, [
    idToUse,
    hidden,
    translations.ariaLabelShow,
    translations.ariaLabelHide,
    size,
    disabled,
    sharedContext.skeleton,
    preventFocusChange,
    toggleVisibility,
  ])

  return (
    <StringField
      id={idToUse}
      className={clsx('dnb-forms-field-password', className)}
      label={label ?? translations.label}
      type={hidden ? 'password' : 'text'}
      value={value}
      ref={ref}
      aria-describedby={idToUse + '-submit-button'}
      submitElement={toggleVisibilityButton}
      disabled={disabled}
      size={size}
      autoComplete="current-password"
      errorMessages={errorMessages}
      {...props}
    />
  )
}

export default Password

withComponentMarkers(Password, { _supportsSpacingProps: true })
